import type { TableFilterValues } from "@kontextwerk/web-sdk/ui/layout/Table.svelte"
import {
  createDefaultWorkOrderFilters,
  formatDateParam,
  isDateRangeFilterEmpty,
  normalizeDateRangeFilter,
  type WorkOrderFilterValue,
} from "../work-order-filters"

export const normalizeStringList = (value: unknown) => {
  const list = Array.isArray(value) ? value : value ? [value] : []

  return list.map((entry) => `${entry ?? ""}`.trim()).filter(Boolean)
}

export const isDefaultWorkOrderTableFilter = (
  values: TableFilterValues | undefined,
  currentFilters: WorkOrderFilterValue,
) => {
  const defaults = createDefaultWorkOrderFilters()
  const searchQuery =
    (values?.searchQuery as string | null | undefined) ?? currentFilters.searchQuery ?? ""
  const orderTypeIds = normalizeStringList(values?.orderTypeIds ?? currentFilters.orderTypeIds)
  const appointmentDateRange = normalizeDateRangeFilter(
    (values?.appointmentDateRange as WorkOrderFilterValue["appointmentDateRange"] | undefined) ??
      currentFilters.appointmentDateRange,
  )
  const dueDateRange = normalizeDateRangeFilter(
    (values?.dueDateRange as WorkOrderFilterValue["dueDateRange"] | undefined) ??
      currentFilters.dueDateRange,
  )
  const employeeIds = normalizeStringList(values?.employeeIds ?? currentFilters.employeeIds)
  const workOrderStates = normalizeStringList(
    values?.workOrderStates ?? currentFilters.workOrderStates,
  )
  const actionRequiredState = (
    (values?.actionRequiredState as string | null | undefined) ??
    currentFilters.actionRequiredState ??
    ""
  ).trim()

  return (
    searchQuery.trim() === defaults.searchQuery &&
    orderTypeIds.length === 0 &&
    isDateRangeFilterEmpty(appointmentDateRange) &&
    employeeIds.length === 0 &&
    isDateRangeFilterEmpty(dueDateRange) &&
    workOrderStates.length === 0 &&
    actionRequiredState === defaults.actionRequiredState
  )
}

export const buildWorkOrderTableQuery = ({
  organizationId,
  values,
  currentFilters,
  offset,
  limit,
}: {
  organizationId?: string | null
  values: TableFilterValues
  currentFilters: WorkOrderFilterValue
  offset: number
  limit: number
}) => {
  if (!organizationId) return null

  const trimmedSearchQuery = ((values.searchQuery as string | null | undefined) ?? "").trim()
  const orderTypeIds = normalizeStringList(values.orderTypeIds ?? currentFilters.orderTypeIds)
  const appointmentDateRange = normalizeDateRangeFilter(
    (values.appointmentDateRange as WorkOrderFilterValue["appointmentDateRange"] | undefined) ??
      currentFilters.appointmentDateRange,
  )
  const dueDateRange = normalizeDateRangeFilter(
    (values.dueDateRange as WorkOrderFilterValue["dueDateRange"] | undefined) ??
      currentFilters.dueDateRange,
  )
  const employeeIds = normalizeStringList(values.employeeIds ?? currentFilters.employeeIds)
  const normalizedWorkOrderStates = Array.from(
    new Set(
      normalizeStringList(values.workOrderStates ?? currentFilters.workOrderStates).map((entry) =>
        entry.toUpperCase(),
      ),
    ),
  )
  const actionRequiredState = (
    (values.actionRequiredState as string | null | undefined) ??
    currentFilters.actionRequiredState ??
    ""
  )
    .trim()
    .toLowerCase()
  const archiveScope =
    (values.archiveScope as ArchiveScope | undefined) ?? currentFilters.archiveScope

  return {
    organizationId,
    searchQuery: trimmedSearchQuery || undefined,
    orderTypeIds: orderTypeIds.length ? orderTypeIds : undefined,
    appointmentDateFrom: formatDateParam(appointmentDateRange.start),
    appointmentDateTo: formatDateParam(appointmentDateRange.end),
    employeeIds: employeeIds.length ? employeeIds : undefined,
    workOrderStates: normalizedWorkOrderStates.length ? normalizedWorkOrderStates : undefined,
    actionRequiredState: actionRequiredState || undefined,
    dueDateFrom: formatDateParam(dueDateRange.start),
    dueDateTo: formatDateParam(dueDateRange.end),
    archiveScope,
    offset,
    limit,
  }
}
